import { useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Chip } from "@/components/ui/chip";
import { FindingRow } from "@/components/experience/finding-row";
import {
  FindingFixDropdown,
  FindingTriageMenu,
} from "@/components/experience/finding-actions";
import { ProviderIcon } from "@/components/workflow/workflow-rail";
import { linkNoun, linkStateChip } from "@/components/workflow/workflow-labels";
import { TargetCropThumbnail } from "../zen-mode/target-crop-thumbnail";
import { isProblemType } from "@iamu/shared";
import { cn } from "@/lib/utils";
import type { WalkthroughFinding } from "./workspace-types";

interface WorkspaceFindingsListProps {
  filteredFindings: WalkthroughFinding[];
  walkthroughSteps?: Array<{ id: string; stepNumber: number }>;
  selectedIds: Set<string>;
  focusedIndex: number;
  allFilteredSelected: boolean;
  someFilteredSelected: boolean;
  canEdit: boolean;
  projectId: string;
  hasLinkedRepo: boolean;
  onToggleSelectAll: () => void;
  onToggleSelect: (id: string, checked: boolean) => void;
  onOpenFinding: (id: string) => void;
  onOpenZenMode?: (index: number) => void;
}

export function WorkspaceFindingsList({
  filteredFindings,
  walkthroughSteps,
  selectedIds,
  focusedIndex,
  allFilteredSelected,
  someFilteredSelected,
  canEdit,
  projectId,
  hasLinkedRepo,
  onToggleSelectAll,
  onToggleSelect,
  onOpenFinding,
  onOpenZenMode,
}: WorkspaceFindingsListProps) {
  const stepMap = useMemo(() => {
    if (!walkthroughSteps) return null;
    return new Map(walkthroughSteps.map((s) => [s.id, s.stepNumber]));
  }, [walkthroughSteps]);
  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Table Column Header with Select All */}
      <div className="flex items-center justify-between border-b border-border/80 bg-muted/40 px-4 py-2.5 text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          {canEdit && (
            <Checkbox
              checked={
                allFilteredSelected
                  ? true
                  : someFilteredSelected
                    ? "indeterminate"
                    : false
              }
              onCheckedChange={onToggleSelectAll}
              aria-label="Select all findings"
            />
          )}
          <span className="font-medium text-foreground">
            {selectedIds.size > 0
              ? `${selectedIds.size} of ${filteredFindings.length} selected`
              : "Finding & Severity"}
          </span>
        </div>
        <span className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
          <kbd className="rounded border border-border/80 bg-background px-1.5 py-0.5 font-mono text-foreground font-semibold">
            j
          </kbd>
          /
          <kbd className="rounded border border-border/80 bg-background px-1.5 py-0.5 font-mono text-foreground font-semibold">
            k
          </kbd>
          <span>navigate</span>
          <span className="text-border">·</span>
          <kbd className="rounded border border-border/80 bg-background px-1.5 py-0.5 font-mono text-foreground font-semibold">
            x
          </kbd>
          <span>select</span>
        </span>
      </div>

      <ul className="divide-y divide-border/60">
        {filteredFindings.map((finding, idx) => {
          const {
            id: fId,
            type: fType,
            severity: fSeverity,
            triageStatus: fTriageStatus,
            fixStatus: fFixStatus,
            lens: fLens,
            title: fTitle,
            description: fDescription,
          } = finding;
          const isMuted = fTriageStatus === "dismissed";
          const isSelected = selectedIds.has(fId);
          const isFocused = idx === focusedIndex;
          const effectiveStepNumber =
            finding.stepNumber ??
            (finding.walkthroughStepId && stepMap
              ? stepMap.get(finding.walkthroughStepId)
              : undefined) ??
            finding.steps?.[0];

          return (
            <li
              key={fId}
              onClick={() => onOpenFinding(fId)}
              className={cn(
                "group flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/15",
                isSelected && "bg-muted/30",
                isFocused && "ring-1 ring-inset ring-primary/40 bg-muted/20",
              )}
            >
              {canEdit && (
                <div className="pt-1" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) =>
                      onToggleSelect(fId, !!checked)
                    }
                    aria-label={`Select finding: ${fTitle ?? fDescription.slice(0, 40)}`}
                  />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <FindingRow
                  finding={{
                    id: fId,
                    title: fTitle,
                    description: fDescription,
                    what: finding.what,
                    why: finding.why,
                    breaksRule:
                      finding.breaksRule ??
                      finding.designCauses?.[0]?.breaksRule ??
                      null,
                    breaksFile:
                      finding.breaksFile ??
                      finding.designCauses?.[0]?.breaksFile ??
                      null,
                    screen: finding.screen,
                    type: fType,
                    severity: fSeverity,
                    fixStatus: fFixStatus,
                    lens: fLens,
                    walkthroughId: finding.walkthroughId,
                    stepNumber: effectiveStepNumber,
                    targetBox: finding.targetBox,
                  }}
                  muted={isMuted}
                  trailing={
                    finding.walkthroughId &&
                    typeof effectiveStepNumber === "number" ? (
                      <div
                        className="hidden md:block h-12 w-20 shrink-0 overflow-hidden rounded border border-border/60 hover:border-border hover:shadow-xs transition-all cursor-zoom-in group/thumb"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onOpenZenMode) {
                            document.documentElement
                              .requestFullscreen?.()
                              .catch(() => {});
                            onOpenZenMode(idx);
                          } else {
                            onOpenFinding(fId);
                          }
                        }}
                        title="Click to inspect in Zen Carousel"
                      >
                        <TargetCropThumbnail
                          walkthroughId={finding.walkthroughId}
                          stepNumber={effectiveStepNumber}
                          targetBox={finding.targetBox}
                          targetTone={fSeverity || fType}
                          alt={`Step ${effectiveStepNumber} target preview`}
                          className="h-full w-full"
                        />
                      </div>
                    ) : undefined
                  }
                  meta={
                    (finding.designCauses && finding.designCauses.length > 0) ||
                    (finding.trackerLinks &&
                      finding.trackerLinks.length > 0) ? (
                      <div className="flex flex-wrap items-center gap-1.5">
                        {finding.designCauses &&
                          finding.designCauses.length > 0 && (
                            <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground">
                              + Design cause
                            </span>
                          )}
                        {finding.trackerLinks &&
                          finding.trackerLinks.length > 0 && (
                            <div
                              className="flex items-center gap-1.5"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {finding.trackerLinks.map((link) => {
                                const chip = linkStateChip(link);
                                return (
                                  <a
                                    key={link.id}
                                    href={link.url ?? undefined}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 rounded border border-border bg-card px-1.5 py-0.5 text-2xs font-medium hover:bg-muted transition-colors text-foreground"
                                  >
                                    <ProviderIcon
                                      provider={link.provider}
                                      className="size-3"
                                    />
                                    <span>
                                      {link.displayKey ?? linkNoun(link.kind)}
                                    </span>
                                    <Chip
                                      tone={chip.tone}
                                      className="text-2xs py-0 px-1 h-3.5"
                                    >
                                      {chip.label}
                                    </Chip>
                                  </a>
                                );
                              })}
                            </div>
                          )}
                      </div>
                    ) : undefined
                  }
                  actions={
                    <>
                      {canEdit && (
                        <div
                          className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {isProblemType(fType) && fTriageStatus === "open" && (
                            <FindingFixDropdown
                              findingId={fId}
                              projectId={projectId}
                              hasLinkedRepo={hasLinkedRepo}
                              size="xs"
                              variant="outline"
                            />
                          )}
                          <FindingTriageMenu
                            findingId={finding.canonicalFindingId ?? fId}
                            projectId={projectId}
                            state={
                              fTriageStatus === "addressed"
                                ? "fix_applied"
                                : fTriageStatus === "deferred"
                                  ? "snoozed"
                                  : fTriageStatus === "wont_fix"
                                    ? "risk_accepted"
                                    : fTriageStatus
                            }
                            findingType={fType}
                            subject={fTitle ?? fDescription.slice(0, 60)}
                            size="xs"
                            iconOnly
                          />
                        </div>
                      )}
                      {fTriageStatus !== "open" && (
                        <Badge variant="secondary">
                          {fTriageStatus === "dismissed"
                            ? "Dismissed"
                            : fTriageStatus === "addressed"
                              ? "Addressed"
                              : fTriageStatus === "deferred"
                                ? "Deferred"
                                : fTriageStatus === "wont_fix"
                                  ? "Won't fix"
                                  : fTriageStatus}
                        </Badge>
                      )}
                    </>
                  }
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
